/**
 * The board's clock, in one place.
 *
 * Production: `now()` is `new Date()` and nothing else. In dev it can be
 * shifted by `?at=HH:MM` so any point of the day arc is previewable without
 * waiting for 22:00 — the shift moves *everything* that reads the clock (the
 * arc face, the stage clock, the poll taper), so a forced 22:30 behaves like
 * a real 22:30 rather than only repainting.
 *
 * The offset is same-day by construction: forcing 01:30 from an afternoon
 * winds the clock *back* to this morning rather than forward into tomorrow,
 * so the board's local date — and therefore every "today" the server already
 * answered with — stays put.
 */

let offsetMs = 0;

export function now(): Date {
  return offsetMs === 0 ? new Date() : new Date(Date.now() + offsetMs);
}

/** The active dev shift in ms (0 in production). */
export function clockOffsetMs(): number {
  return offsetMs;
}

export function setClockOffsetMs(ms: number): void {
  offsetMs = Number.isFinite(ms) ? ms : 0;
}

/**
 * Offset that makes `now` read as local `HH:MM` on the same calendar day.
 * Returns null for anything that is not a wall-clock time.
 */
export function offsetForClock(target: string, base: Date): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(target.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  const todayMs =
    base.getHours() * 3_600_000 +
    base.getMinutes() * 60_000 +
    base.getSeconds() * 1000 +
    base.getMilliseconds();
  return h * 3_600_000 + min * 60_000 - todayMs;
}

/** Dev only: apply `?at=HH:MM` from the URL. No-op when absent or malformed. */
export function applyDevClockParam(search: string): number {
  const at = new URLSearchParams(search).get('at');
  const off = at ? offsetForClock(at, new Date()) : null;
  setClockOffsetMs(off ?? 0);
  return offsetMs;
}
